import React, { useEffect, useState, useCallback } from "react"
import { ErrorBoundary } from "../ErrorBoundary"
import { qs, StatCard, PivotTable, Trend, Donut, Notice, FilterBar, FunnelChips, ConversionPivot } from "./components"

// Traffic Sources overview (doc 11): headline cards, trend, and dimension
// pivots. The row-level data lives on the separate Reports page (linked below).
export default function TrafficApp({ trafficPath, reportsPath, integrationsPath, copy }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [draft, setDraft] = useState({})
  const [applied, setApplied] = useState({})

  const load = useCallback(() => {
    const sep = trafficPath.includes("?") ? "&" : "?"
    fetch(`${trafficPath}${sep}${qs(applied)}`, { headers: { Accept: "application/json" } })
      .then((r) => r.json())
      .then(setData)
      .finally(() => setLoading(false))
  }, [trafficPath, applied])

  useEffect(() => { load() }, [load])

  const header = (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{copy.title}</h1>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{copy.subtitle}</p>
    </div>
  )

  if (loading) return <ErrorBoundary><div className="space-y-6">{header}<p className="text-sm text-gray-500 dark:text-gray-400">…</p></div></ErrorBoundary>

  if (!data || !data.connected) {
    const notice = data && data.status === "invalid" ? copy.invalid : copy.empty
    return <ErrorBoundary><div className="space-y-6">{header}<Notice {...notice} href={integrationsPath} /></div></ErrorBoundary>
  }
  if ((data.totals?.views ?? 0) === 0 && Object.keys(applied).length === 0) {
    return <ErrorBoundary><div className="space-y-6">{header}<Notice {...copy.noData} /></div></ErrorBoundary>
  }

  const options = {
    apps: (data.byApp || []).map((r) => r.label),
    countries: (data.byCountry || []).map((r) => r.label),
    referrers: (data.byReferrer || []).map((r) => r.label),
  }

  return (
    <ErrorBoundary>
      <div className="space-y-6">
        {header}
        <FilterBar
          draft={draft}
          setDraft={setDraft}
          options={options}
          onApply={() => setApplied(draft)}
          onPreset={(from, to) => {
            const next = { ...draft, from, to }
            setDraft(next)
            setApplied(next)
          }}
          onClear={() => { setDraft({}); setApplied({}) }}
          copy={copy.filters}
        />
        {data.funnel && <FunnelChips funnel={data.funnel} copy={copy.funnel} />}
        <dl className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard label={copy.totals.views} value={data.totals.views} />
          <StatCard label={copy.totals.referrers} value={data.totals.referrers} />
          <StatCard label={copy.totals.countries} value={data.totals.countries} />
          <StatCard label={copy.totals.searchTerms} value={data.totals.searchTerms} />
        </dl>
        {data.funnel && <ConversionPivot rows={data.funnel.byReferrer} copy={copy.conversion} />}
        <Trend title={copy.sections.trend} points={data.trend} />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Donut title={copy.sections.byReferrer} rows={data.byReferrer} />
          <Donut title={copy.sections.byCountry} rows={data.byCountry} />
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <PivotTable title={copy.sections.byReferrer} rows={data.byReferrer} copy={copy.table} />
          <PivotTable title={copy.sections.byCountry} rows={data.byCountry} copy={copy.table} />
          <PivotTable title={copy.sections.bySearch} rows={data.bySearch} copy={copy.table} />
          <PivotTable title={copy.sections.byApp} rows={data.byApp} copy={copy.table} />
        </div>
      </div>
    </ErrorBoundary>
  )
}
